// Flavors — sabores típicos por ciudad
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, Navigation, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState, useRef, useMemo } from "react";
import { TRIP as STATIC_TRIP } from "../data/trip";
import { useGeolocation, formatDistance, walkingMinutes } from "../hooks/useGeolocation";
import { nearestVenue } from "../hooks/useProximityAlerts";
import FavoriteButton from "../components/FavoriteButton";
import { useTripBase } from "../hooks/useTripBase";
import { useTrip } from "../auth/TripContext";

const DEFAULT_CITIES = ["Budapest", "Praga", "Tel Aviv", "Madrid"];

// Convert a wizard restaurant into a Flavor-style item so we can render
// signature dishes as "sabores típicos" when no explicit `tastings` exists.
function restaurantToFlavor(r) {
  if (!r) return null;
  return {
    id: `flavor-from-${r.id}`,
    name: r.signature || r.name,
    emoji: r.cuisine ? "🍽️" : "🍰",
    description: r.why_special || r.signature || `${r.cuisine || "Especialidad"} de ${r.name}`,
    tip: r.local_tip || r.tip || null,
    venues: (r.lat && r.lng) ? [{
      id: `v-${r.id}`,
      name: r.name,
      area: r.area || "",
      address: r.address || "",
      lat: r.lat,
      lng: r.lng,
      price: r.price,
    }] : [],
  };
}

export default function Flavors({ hideBackLink = false }) {
  const base = useTripBase();
  const [searchParams] = useSearchParams();
  const { trip } = useTrip();
  const T = trip && trip.restaurants ? trip : {};
  // Build cities + items dynamically. Prefer explicit `tastings` (legacy),
  // fallback to synthesizing from restaurants' signature dishes (wizard trips).
  const { CITIES, itemsByCity } = useMemo(() => {
    const tastingsCities = Object.keys(T.tastings || {});
    const restaurantCities = Object.keys(T.restaurants || {});
    const cities = tastingsCities.length ? tastingsCities : (restaurantCities.length ? restaurantCities : DEFAULT_CITIES);
    const map = {};
    for (const c of cities) {
      if (T.tastings?.[c]?.length) {
        map[c] = T.tastings[c];
      } else {
        const rsts = T.restaurants?.[c] || [];
        map[c] = rsts.map(restaurantToFlavor).filter(Boolean);
      }
    }
    return { CITIES: cities, itemsByCity: map };
  }, [T]);

  const focusId = searchParams.get("focus");
  const initialCity = searchParams.get("city") || CITIES[0];
  const [city, setCity] = useState(
    CITIES.includes(initialCity) ? initialCity : CITIES[0]
  );
  const items = itemsByCity[city] || [];
  const { position } = useGeolocation();
  const focusRef = useRef(null);

  // Scroll a la tarjeta enfocada si llega ?focus=t-xxx
  useEffect(() => {
    if (focusId && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusId, city]);

  const openInMaps = (venue) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${venue.lat},${venue.lng}&travelmode=walking`;
    window.open(url, "_blank");
  };

  return (
    <div className="container-app pt-6 pb-28" data-testid="flavors-page">
      {!hideBackLink && (
        <Link to={base || "/"} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-4" data-testid="flavors-back">
          <ChevronLeft size={16} /> Inicio
        </Link>
      )}
      <h1 className="text-3xl font-bold leading-tight">Sabores típicos</h1>
      <p className="text-sm text-[hsl(var(--muted))] mt-1 mb-5">Lo que hay que probar y dónde encontrarlo</p>

      {/* City switcher */}
      <div className="flex gap-2 mb-5 overflow-x-auto hide-scrollbar">
        {CITIES.map((c) => (
          <button
            key={c}
            onClick={() => setCity(c)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              city === c
                ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
                : "bg-[hsl(var(--bg-card))] text-[hsl(var(--ink))] border border-[hsl(var(--line))]"
            }`}
            data-testid={`flavors-city-${c}`}
          >
            {c}
          </button>
        ))}
      </div>

      {position && (
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[hsl(var(--accent))] mb-3">
          <MapPin size={11} />
          <span>Ubicación activa · te avisaremos cuando estés cerca de un sabor</span>
        </div>
      )}

      <ul className="space-y-3">
        {items.map((t, i) => {
          const near = position ? nearestVenue(position, t) : null;
          const isFocused = focusId === t.id;
          return (
            <motion.li
              key={t.id}
              ref={isFocused ? focusRef : null}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.04 * i }}
              className={`card-paper p-5 ${
                isFocused ? "ring-2 ring-[hsl(var(--accent))]" : ""
              }`}
              data-testid={`flavor-${t.id}`}
            >
              <div className="flex items-start gap-3 mb-2">
                <span className="text-3xl shrink-0">{t.emoji}</span>
                <div className="flex-1">
                  <h3 className="text-lg font-bold leading-tight">{t.name}</h3>
                </div>
                <FavoriteButton kind="tasting" ref={t.id} />
              </div>
              <p className="text-sm serif text-[hsl(var(--ink-soft))] leading-[1.6] mb-3">{t.what}</p>

              {/* Más cercano */}
              {near && (
                <div
                  className="card-paper p-3 mb-2 border-l-2 border-[hsl(var(--accent))]"
                  style={{ background: "hsl(var(--bg))" }}
                  data-testid={`flavor-nearest-${t.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--accent))] mb-0.5">
                        📍 Más cercano a ti
                      </p>
                      <p className="text-sm font-semibold truncate">{near.name}</p>
                      <p className="text-xs text-[hsl(var(--muted))] mt-0.5">
                        {formatDistance(near.distance)}
                        {walkingMinutes(near.distance) != null &&
                          ` · ~${walkingMinutes(near.distance)} min a pie`}
                      </p>
                    </div>
                    <button
                      onClick={() => openInMaps(near)}
                      className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] text-xs font-semibold active:scale-95"
                      data-testid={`flavor-go-${t.id}`}
                    >
                      <Navigation size={12} /> Llévame
                    </button>
                  </div>
                </div>
              )}

              <div className="card-paper p-3 mb-2" style={{ background: "hsl(var(--bg))" }}>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted))] mb-1">📍 Dónde probarlo</p>
                <p className="text-sm">{t.where}</p>
                {t.venues && t.venues.length > 1 && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-[hsl(var(--accent))] cursor-pointer select-none">
                      Ver {t.venues.length} lugares con mapa
                    </summary>
                    <ul className="mt-2 space-y-1.5">
                      {t.venues.map((v, k) => (
                        <li key={k} className="flex items-center justify-between gap-2 text-xs">
                          <span className="truncate text-[hsl(var(--ink-soft))]">{v.name}</span>
                          <button
                            onClick={() => openInMaps(v)}
                            className="shrink-0 text-[hsl(var(--accent))] inline-flex items-center gap-1"
                            data-testid={`flavor-venue-${t.id}-${k}`}
                          >
                            <Navigation size={11} /> Ir
                          </button>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              {t.tip && (
                <p className="text-xs text-[hsl(var(--accent))] mt-2">💡 {t.tip}</p>
              )}
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}
